import * as React from 'react';
import Grid from '@material-ui/core/Grid';
import Button from '@material-ui/core/Button';
import IconButton from '@material-ui/core/IconButton';
import AddIcon from '@material-ui/icons/Add';
import RemoveIcon from '@material-ui/icons/Remove';
import Typography from '@material-ui/core/Typography';
import FormControlLabel from '@material-ui/core/FormControlLabel';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogTitle from '@material-ui/core/DialogTitle';
import DialogContent from '@material-ui/core/DialogContent';
import MenuItem from '@material-ui/core/MenuItem';
import { Theme, withStyles, WithStyles } from '@material-ui/core/styles';
import { localizations } from '../../localization/localization';
import { renderCheckbox,
    renderNewTextField,
    renderNewTextFieldUpperCase,
    renderMultilineTextField,
    renderErrorInfoText,
    renderNumberSlider,
    renderNumberField,
    renderDatePicker,
    renderTimePicker,
    renderSelectWithTextLabel } from '../../common/forms/FieldCreators';
import nameof from '../../utils/nameof';
import { connect } from 'react-redux';
import { Control, Form, Errors, Fieldset, actions as formActions } from 'react-redux-form'
import { TruckProperties, AddNewTruckModel } from '../TruckModels';
import { RecurrenceModel } from '../../common/models/Recurrence';
import FormItem from '../../components/forms/FormItem';
import WeeklyControls from '../../components/recurrence/WeeklyControls';
import DurationControls from '../../components/recurrence/DurationControls';
import { CitySuggestionDropDown } from '../../components/forms/CitySuggestionDropDown';
import { LoginState } from '../../login/loginStore';
import FormInfo from '../../components/forms/FormInfo';
import { RecurrenceType, weekdaySelected } from '../../common/models/Recurrence';
import { EditMode } from '../../common/models/CommonModels';
import moment from 'moment';
import { UserRole } from '../../login/LoginModels';
import { actionCreators as companiesActions } from '../../admin/store/companiesStore';
import { RegisteredCompanyModel } from '../../admin/models/CompanyModels';

const styles = (theme: Theme) => ({
    radio: {
        fontSize: theme.typography.fontSize,
        fontFamily: theme.typography.fontFamily
    },
    checkBox: {
        marginLeft: 0
    },
    availableSection: {
        marginTop: theme.spacing(4)
    }
});

interface FromInternalState {
    recurring: boolean;
    selectedRecurringType: "daily" | "weekly" | "monthly";
    login: LoginState;
    noDestination: boolean;
    isDistanceCheckEnabled: boolean;
    searchDistance: number;
}

export interface AddFreeTruckFormProps {
    isOpen: boolean;
    onCancel: (event: React.MouseEvent<HTMLElement>) => void;
    onSubmit: (values: AddNewTruckModel) => void;
    isEditMode: boolean;
    selectedRecurrenceLimit: string;
    newTruckForm: any,
    setFormValue: (model: string, value: any) => void;
    login: LoginState;
    requestRegisteredCompanies: () => void;
    companies: RegisteredCompanyModel[];
}

type FormProps = AddFreeTruckFormProps
    & WithStyles<"radio" | "checkBox" | "availableSection">
    & FromInternalState
    & EditMode

interface AddFreeTruckFormState {
    noDestination: boolean;
}

const maxLength1000 = (val: string) => val && val.length > 1000 ? localizations.trucks.maxThousandChars : undefined;



class AddFreeTruckForm extends React.Component<FormProps, AddFreeTruckFormState> {

    constructor(props: FormProps) {
        super(props);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    public componentDidMount() {
        const { login, requestRegisteredCompanies } = this.props;

        if (login.role === UserRole.Admin) {
            requestRegisteredCompanies();
        }
    }

    public componentDidUpdate(prevProps: FormProps) {
        const { login, isOpen, newTruckForm, companies, setFormValue } = this.props;

        if (login.role !== UserRole.Admin) {
            return;
        }

        if ((isOpen && !prevProps.isOpen || companies !== prevProps.companies) && !newTruckForm.companyId.value && !newTruckForm.createdBy.value) {
            const selectedCompanyId = sessionStorage.getItem('selectedCompanyId');
            const selectedCompany = companies.find(company => company.id === selectedCompanyId);
            const selectedUserId = sessionStorage.getItem('selectedUserId');
            const selectedUser = selectedCompany?.employees?.find(employee => employee.id === selectedUserId);

            if (selectedCompany && selectedUser) {
                setFormValue(`newTruck.${nameof<AddNewTruckModel>('companyId')}`, selectedCompany.id);
                setFormValue(`newTruck.${nameof<AddNewTruckModel>('createdBy')}`, selectedUser.id);
            }
        }

        if (newTruckForm.companyId.value !== prevProps.newTruckForm.companyId.value && prevProps.newTruckForm.companyId.value) {
            setFormValue(`newTruck.${nameof<AddNewTruckModel>('createdBy')}`, '');
        }
    }

    public render() {
        const { classes, isOpen, noDestination, newTruckForm, setFormValue, login, companies } = this.props;

        return (
            <Dialog open={isOpen}>
                <Form hideNativeErrors model="newTruck" onSubmit={this.handleSubmit} validators={{
                    "": {
                        hasDeparture: (vals) => {
                            return vals.departureCity && vals.departureCity.name && vals.departureCity.name.length > 0;
                        },
                        hasDestination: (vals) => {
                            return vals.noDestination || (!vals.noDestination && vals.destinationCity && vals.destinationCity.name && vals.destinationCity.name.length > 0)
                        },
                        isWeekdaySelected: (vals) => {
                            return !this.props.recurring || (vals.recurrence.weekdays && weekdaySelected(vals.recurrence.weekdays));
                        },
                        availableFromIsBeforeAvailableTo: (vals) => {
                            if (vals.availableFrom) {
                                return vals.availableFrom.isBefore(vals.availableTo);
                            }
                            return false;
                        },
                        detailsMaxLength: (vals) => {
                            return !vals.details || vals.details.length <= 1000;
                        },
                        distanceOutsideBounds: (vals) => {
                            return !this.props.isDistanceCheckEnabled || (Number(vals.searchDistance) >= 0 && Number(vals.searchDistance) <= 200);
                        },
                        hasUser: (vals) => {
                            return !vals.companyId || vals.companyId && vals.createdBy;
                        }
                    }
                }}>
                    <DialogTitle style={{ textAlign: "center" }}>
                        <Typography variant="h4" paragraph>
                            {this.props.editMode === "add" ? localizations.trucks.reportNewTruck : null}
                            {this.props.editMode === "edit" ? localizations.trucks.editTruckTitle : null}
                            {this.props.editMode === "copy" ? localizations.trucks.actions.copy : null}
                        </Typography>
                    </DialogTitle>
                    <DialogContent>
                        <Grid item>
                            <Grid container direction="column" alignItems="center">
                                <FormInfo infoText={localizations.trucks.fillInTruckInformation} />
                                <FormItem>
                                    <FormItem>
                                        <Control
                                            model={`.${nameof<AddNewTruckModel>("departureCity")}`}
                                            component={CitySuggestionDropDown}
                                            required
                                            label={localizations.common.pointOfDeparture}
                                        />
                                    </FormItem>
                                    <Errors
                                        model="newTruck"
                                        show={{ touched: true, focus: false }}
                                        messages={{ hasDeparture: localizations.validation.required }}
                                        wrapper={renderErrorInfoText}
                                    />
                                    <FormItem>
                                        <FormControlLabel
                                            control={
                                                <Control.checkbox
                                                    model={`.${nameof<AddNewTruckModel>("noDestination")}`}
                                                    component={renderCheckbox}
                                                />
                                            }
                                            label={localizations.trucks.noPointOfArrival}
                                        />
                                        <Control
                                            model={`.${nameof<AddNewTruckModel>("destinationCity")}`}
                                            component={CitySuggestionDropDown}
                                            disabled={noDestination}
                                            required={!noDestination}
                                            label={localizations.common.pointOfArrival}
                                        />
                                    </FormItem>
                                    <Errors
                                        model="newTruck"
                                        show={{ touched: true, focus: false }}
                                        messages={{ hasDestination: localizations.validation.required }}
                                        wrapper={renderErrorInfoText}
                                    />
                                </FormItem>
                                <Grid className={classes.availableSection} container spacing={2}>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddNewTruckModel>("availableFrom")}`}
                                                component={renderDatePicker}
                                                required
                                                label={localizations.trucks.availableFromDate}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.next}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableFrom")}`, moment(newTruckForm.availableFrom.value).add(1, "days"))}>
                                                <AddIcon />
                                            </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.previous}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableFrom")}`, moment(newTruckForm.availableFrom.value).subtract(1, "days"))}>
                                                <RemoveIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddNewTruckModel>("availableFrom")}`}
                                                component={renderTimePicker}
                                                required
                                                label={localizations.trucks.availableFromTime}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.next}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableFrom")}`, moment(newTruckForm.availableFrom.value).add(1, "hours"))}>
                                                <AddIcon />
                                            </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.previous}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableFrom")}`, moment(newTruckForm.availableFrom.value).subtract(1, "hours"))}>
                                                <RemoveIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddNewTruckModel>("availableTo")}`}
                                                component={renderDatePicker}
                                                required
                                                label={localizations.trucks.availableToDate}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.next}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableTo")}`, moment(newTruckForm.availableTo.value).add(1, "days"))}>
                                                <AddIcon />
                                            </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.previous}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableTo")}`, moment(newTruckForm.availableTo.value).subtract(1, "days"))}>
                                                <RemoveIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid container item xs={12}>
                                        <Grid item xs>
                                            <Control
                                                model={`.${nameof<AddNewTruckModel>("availableTo")}`}
                                                component={renderTimePicker}
                                                required
                                                label={localizations.trucks.availableToTime}
                                            />
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.next}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableTo")}`, moment(newTruckForm.availableTo.value).add(1, "hours"))}>
                                                <AddIcon />
                                            </IconButton>
                                        </Grid>
                                        <Grid item xs={1}>
                                            <IconButton
                                            aria-label={localizations.actions.previous}
                                            onClick={() => setFormValue(`newTruck.${nameof<AddNewTruckModel>("availableTo")}`, moment(newTruckForm.availableTo.value).subtract(1, "hours"))}>
                                                <RemoveIcon />
                                            </IconButton>
                                        </Grid>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Errors
                                        model="newTruck"
                                        show={{ touched: true, focus: false }}
                                        messages={{ availableFromIsBeforeAvailableTo: localizations.validation.endTimeAfterStartTime }}
                                            wrapper={renderErrorInfoText}
                                        />
                                    </Grid>
                                </Grid>
                                <FormItem>
                                    <FormItem>
                                        <Control.text
                                            model={`.${nameof<AddNewTruckModel>("plateNumber")}`}
                                            component={renderNewTextFieldUpperCase}
                                            label={localizations.trucks.licensePlateNumber}
                                        />
                                        <Errors
                                            model={`.${nameof<AddNewTruckModel>("plateNumber")}`}
                                            wrapper={renderErrorInfoText}
                                            show={{ touched: true, focus: false }}
                                        />
                                    </FormItem>
                                    <FormItem>
                                        <Control.text
                                            model={`.${nameof<AddNewTruckModel>("carrierCompany")}`}
                                            component={renderNewTextField}
                                            label={localizations.trucks.carrierCompany}
                                        />
                                    </FormItem>
                                    <FormItem>
                                        <FormItem>
                                            <Control.text
                                                model={`.${nameof<AddNewTruckModel>("contractPrice")}`}
                                                component={renderNumberField}
                                                label={localizations.trucks.contractPrice}
                                            />
                                        </FormItem>
                                    </FormItem>
                                </FormItem>
                                <FormItem>
                                    <FormItem>
                                        <Typography align="left" variant="body2">{localizations.common.properties}</Typography>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("trailer")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.units.trailer}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("container20")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.container20}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("containerOver20")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.containerOver20}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("pieceGoods")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.pieceGoods}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("bulkGoods")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.bulkGoods}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("thermal")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.thermal}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("tank")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.tank}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("glass")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.glass}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("mega")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.mega}
                                                        />
                                                    </td>
                                                </tr>
                                                <tr>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("adr")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.adr}
                                                        />
                                                    </td>
                                                    <td>
                                                        <FormControlLabel
                                                            className={classes.checkBox}
                                                            control={
                                                                <Control.checkbox
                                                                    model={`.${nameof<AddNewTruckModel>("properties")}.${nameof<TruckProperties>("explosive")}`}
                                                                    component={renderCheckbox}
                                                                />
                                                            }
                                                            label={localizations.trucks.properties.explosive}
                                                        />
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </table>
                                    </FormItem>
                                </FormItem>
                                <FormItem>
                                    <Control.text
                                        model={`.${nameof<AddNewTruckModel>("details")}`}
                                        component={renderMultilineTextField}
                                        label={localizations.common.details}
                                    />
                                    <Errors
                                        model="newTruck"
                                        show={{ touched: true, focus: false }}
                                        messages={{ detailsMaxLength: localizations.validation.maxLength1000 }}
                                        wrapper={renderErrorInfoText}
                                    />
                                </FormItem>
                                <FormItem>
                                    <FormControlLabel
                                        control={
                                            <Control.checkbox
                                                model={`.${nameof<AddNewTruckModel>("isDistanceCheckEnabled")}`}
                                                component={renderCheckbox}
                                                checked={this.props.isDistanceCheckEnabled}
                                                title={localizations.common.activateTruckCheckTooltip}
                                            />
                                        }
                                        label={localizations.common.activateDistanceCheck}
                                    />
                                </FormItem>
                                {this.props.isDistanceCheckEnabled ? this.getDistanceCheckControls() : null}
                                <FormItem>
                                    <FormControlLabel
                                        control={
                                            <Control.checkbox
                                                model=".recurrence.recurring"
                                                component={renderCheckbox}
                                                checked={this.props.recurring}
                                                disabled={this.props.editMode === "edit"}
                                            />
                                        }
                                        label={localizations.trucks.weeklyRecurringAdd}
                                    />
                                </FormItem>
                                {this.props.recurring ? this.getRecurringControls() : null}
                                {login.role === UserRole.Admin &&
                                <>
                                    <FormItem>
                                        <Control.text
                                            label={localizations.company.company}
                                            model={`.${nameof<AddNewTruckModel>("companyId")}`}
                                            component={renderSelectWithTextLabel} 
                                            id="addNewTruckForm-companyId-select"
                                            controlProps={{
                                                fullWidth: true
                                            }}
                                        >
                                            <MenuItem value="">{localizations.company.ownCompany}</MenuItem>
                                            {companies.map(company => <MenuItem key={company.id} value={company.id}>{company.name}</MenuItem>)}
                                        </Control.text>
                                    </FormItem>
                                    <FormItem>
                                        <Control.text
                                            label={localizations.common.organizer}
                                            model={`.${nameof<AddNewTruckModel>("createdBy")}`}
                                            component={renderSelectWithTextLabel} 
                                            id="addNewTruckForm-createdBy-select"
                                            controlProps={{
                                                fullWidth: true
                                            }}
                                        >
                                            <MenuItem value="">{localizations.common.user}</MenuItem>
                                            {companies.find(company => company.id === newTruckForm.companyId.value)?.employees?.map(user => <MenuItem key={user.id} value={user.id}>{`${user.firstName} ${user.lastName}`}</MenuItem>)}
                                        </Control.text>
                                        <Errors
                                            model="newTruck"
                                            show={{ touched: true, focus: false }}
                                            messages={{ hasUser: localizations.validation.required }}
                                            wrapper={renderErrorInfoText}
                                        />
                                    </FormItem>
                                </>
                                }
                                <DialogActions>
                                    <Grid item>
                                        <Grid container alignItems="center" justify="center" spacing={4}>
                                            <Grid item>
                                                <Button
                                                    variant="contained"
                                                    color="primary"
                                                    onClick={this.props.onCancel}
                                                >
                                                    {localizations.actions.cancel}
                                                </Button>
                                            </Grid>
                                            <Grid item>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    color="secondary"
                                                >
                                                    {localizations.actions.save}
                                                </Button>
                                            </Grid>
                                        </Grid>
                                    </Grid>
                                </DialogActions>
                            </Grid>
                        </Grid>
                    </DialogContent>
                </Form>
            </Dialog>
        );
    }

    private handleSubmit(values: AddNewTruckModel) {
        const { login, onSubmit } = this.props;

        if (login.role === UserRole.Admin
            && values.companyId
            && values.companyId !== login.companyId
            && values.createdBy
            && values.createdBy !== login.userId) {
            sessionStorage.setItem('selectedCompanyId', values.companyId);
            sessionStorage.setItem('selectedUserId', values.createdBy);
        }

        // Inject user and company information to add request.
        if (login.token) {
            const data = Object.assign({}, values, { companyId: values.companyId || login.companyId, createdBy: values.createdBy || login.token.sub })

            onSubmit(data);
        }
    }

    private getDistanceCheckControls() {
        return (
            <FormItem>
                <Typography align="left" variant="body2">{localizations.common.searchDistance}</Typography>
                <br />
                <FormItem>
                    <Control
                        model={`.${nameof<AddNewTruckModel>("searchDistance")}`}
                        component={renderNumberSlider}
                    />
                </FormItem>
            </FormItem>
        );
    }

    private getRecurringControls() {
        const { newTruckForm, setFormValue } = this.props;

        return (
            <FormItem>
                {/*<FormItem>
                    <Field model=".recurrence.type" dynamic={false}>
                        <FormControlLabel control={<Control.radio model=".recurrence.type" component={renderRadioButton} value="daily" />} label={localizations.recurrence.daily} />
                        <FormControlLabel control={<Control.radio model=".recurrence.type" component={renderRadioButton} value="weekly" />} label={localizations.recurrence.weekly} />
                        <FormControlLabel control={<Control.radio model=".recurrence.type" component={renderRadioButton} value="monthly" />} label={localizations.recurrence.monthly} />
                    </Field>
                </FormItem>*/}
                <FormItem>
                    <Fieldset model=".recurrence">
                        {this.getRecurringByType(this.props.selectedRecurringType)}
                        <DurationControls
                            modifying={this.props.editMode === "edit"}
                            selectedType={this.props.selectedRecurrenceLimit}
                            startDate={newTruckForm.recurrence.startDate.value}
                            setStartDate={(value: moment.Moment) => setFormValue(`newTruck.recurrence.${nameof<RecurrenceModel>("startDate")}`, value)} />
                    </Fieldset>
                </FormItem>
            </FormItem>
        );
    }

    private getRecurringByType(type: RecurrenceType) {
        return <WeeklyControls errorModelName="newTruck" disabled={this.props.editMode === "edit"} />;
        /* switch (type) {
            case "daily":
                return <DailyControls />
            case "weekly":
                return <WeeklyControls errorModelName="newTruck" />
            case "monthly":
                return <MonthlyControls />
            default:
                const exCheck: never = type;
        }
        return null; */
    }
}

const mapStateToProps = ({ login, newTruck, forms, companies }: any) => {
    return {
        selectedRecurringType: newTruck.recurrence.type,
        recurring: newTruck.recurrence.recurring,
        login,
        noDestination: newTruck.noDestination,
        selectedRecurrenceLimit: forms.newTruck.recurrence.endDateMode.value,
        isDistanceCheckEnabled: newTruck.isDistanceCheckEnabled,
        searchDistance: newTruck.searchDistance,
        newTruckForm: forms.newTruck,
        companies: companies.data,
    }
}

const mapDispatchToProps = (dispatch: any) => {
    return {
      setFormValue: (model: string, value: any) => dispatch(formActions.change(model, value)),
      requestRegisteredCompanies: () => dispatch(companiesActions.requestRegisteredCompanies()),
    }
}

export default withStyles(styles)(connect<FromInternalState, any, any, AddFreeTruckFormProps>(mapStateToProps, mapDispatchToProps)(AddFreeTruckForm));


// WEBPACK FOOTER //
// ./src/trucks/forms/AddFreeTruckForm.tsx